import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { useMutation } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { referenceClient } from "@/lib/orpc/client";
import {
  getVisitScope,
  refreshVisitReads,
  useVisitScope,
  type VisitScope,
} from "@/lib/visit-queries";

const orpc = createTanstackQueryUtils(referenceClient);
type WriteState = {
  scope: VisitScope;
  phase: "pending" | "success" | "error";
};

export function useVisitWrites() {
  const scope = useVisitScope();
  const context = { expectedUserUid: scope?.uid };
  const update = useMutation(orpc.updateVisit.mutationOptions({ context }));
  const remove = useMutation(orpc.deleteVisit.mutationOptions({ context }));
  const note = useMutation(orpc.updateVisitNote.mutationOptions({ context }));
  const [state, setState] = useState<WriteState | null>(null);
  const sequence = useRef(0);
  // Resolves true once the write succeeded and the visit was read again, so
  // a caller can leave edit mode on the saved note; false when it failed or
  // the account changed (the error toast and the draft stay).
  const run = async (write: () => Promise<unknown>): Promise<boolean> => {
    if (!scope || getVisitScope() !== scope) return false;
    const attempt = ++sequence.current;
    const current = (): boolean =>
      getVisitScope() === scope && sequence.current === attempt;
    setState({ scope, phase: "pending" });
    try {
      await write();
      if (!current()) return false;
      setState({ scope, phase: "success" });
      await refreshVisitReads(scope);
      return current();
    } catch {
      if (current()) setState({ scope, phase: "error" });
      return false;
    }
  };
  const phase = state?.scope === scope ? state?.phase : undefined;
  return {
    updateVisit: (
      input: Parameters<typeof referenceClient.updateVisit>[0],
    ): Promise<boolean> => run(() => update.mutateAsync(input)),
    deleteVisit: (
      input: Parameters<typeof referenceClient.deleteVisit>[0],
    ): Promise<boolean> => run(() => remove.mutateAsync(input)),
    updateVisitNote: (
      input: Parameters<typeof referenceClient.updateVisitNote>[0],
    ): Promise<boolean> => run(() => note.mutateAsync(input)),
    loading: phase === "pending",
    result: phase === "success" ? true : phase === "error" ? false : null,
    error: phase === "error",
  };
}
